import {
  DictionarySchema,
  QcBizDictSchema,
  SystemMessageDictStatusSchema,
  TranslationStatusSchema,
} from "@/modules/admin/objects/bizDicts";
import { LangRestrictionStatusSchema } from "@/modules/admin/objects/langRestriction";
import { QcUserSchema } from "@/modules/staff/objects/conversation/QcUser";
import { z } from "zod";
import { AdminGroupSubMenus } from "./AdminGroupSubMenus";

export const EMPTY_TENANT = {
  id: -1,
  code: "",
  name_dict: "",
  description_dict: "",
  lang_cd: "",
  pictures: [],
  logo: "",
  background_image: "",
  qr_invite_staff: "",
  admin_tenant_flg: false,
  is_client: false,
  cc_status_visible: false,
  survey_rating_enabled: false,
  inquiry_reject_enabled: false,
  names: [],
  descriptions: [],
  main_menus: [],
  sub_menus: [],
  location_groups: [],
  widget_on_off: false,
  dch_hotel_id: "",
};

export enum AdminDialogMessage {
  SAVE,
  CUSTOM,
}

export type SubHeaderMenu = z.infer<typeof SubHeaderMenuSchema>;
export const SubHeaderMenuSchema = z.object({
  code: z.string(),
  label: z.string(),
  isDisable: z.boolean().optional(),
  hasDisableCondition: z.boolean().optional(),
});

export type SystemMessageVariable = z.infer<typeof SystemMessageVariableSchema>;
export const SystemMessageVariableSchema = z.object({
  order: z.number(),
  use_translation: z.boolean(),
  code: z.string(),
  dict_cd: z.string(),
  contents: z.string(),
});

export type QueryLocation = z.infer<typeof QueryLocationSchema>;
export const QueryLocationSchema = z.object({
  id: z.number(),
  location_group_id: z.number(),
  name_dict: z.string(),
  qr: z.string(),
  default_name: z.string(),
  name: z.string(),
  hash_value: z.string(),
  // pms_name: z.string().nullable(),
  pms_name: z
    .string()
    .nullable()
    .transform((value) => value ?? ""),
  cms_flag: z.boolean(),
});

export type QueryLocationSubMenu = z.infer<typeof QueryLocationSubMenuSchema>;
export const QueryLocationSubMenuSchema = z.object({
  id: z.number(),
  location_main_menu_id: z.number(),
  sub_menu_id: z.number(),
  order_no: z.number(),
});

export type QueryStaffGroupState = z.infer<typeof QueryStaffGroupStateSchema>;
export const QueryStaffGroupStateSchema = z.object({
  id: z.number(),
  staff_group_id: z.number().nullable(),
  tenant_id: z.number().nullable(),
  name: z.string(),
  order_no: z.number().nullable(),
  online_total: z.number().nullable().optional(),
  busy_total: z.number().nullable().optional(),
});

export type QueryQcBizDict = z.infer<typeof QueryQcBizDictSchema>;
export const QueryQcBizDictSchema = z.object({
  id: z.number(),
  tenant_id: z.number(),
  dict_cd: z.string(),
  lang_cd: z.string(),
  message: z.string(),
});

export type QueryMainMenu = z.infer<typeof QueryMainMenuSchema>;
export const QueryMainMenuSchema = z.object({
  id: z.number(),
  tenant_id: z.number(),
  name_dict: z.string(),
  icon: z.string().nullable(),
  name: z.string(),
  code: z.string(),
  icon_code: z.string(),
  required_show_submenu: z.boolean(),
});

export type MainMenuFavoriteSubMenu = z.infer<typeof MainMenuFavoriteSubMenuSchema>;
export const MainMenuFavoriteSubMenuSchema = z.object({
  main_menu_id: z.number(),
  sub_menu_id: z.number(),
});

export type OutOfServiceStatus = z.infer<typeof OutOfServiceStatusSchema>;
export const OutOfServiceStatusSchema = z.object({
  is_out_of_service: z.boolean(),
  out_of_service_message: z.string(),
  display_timing: z.string(),
});

export type UserDictMessage = z.infer<typeof UserDictMessageSchema>;
export const UserDictMessageSchema = z.object({
  lang_cd: z.string(),
  message: z
    .string()
    .nullable()
    .transform((value) => value ?? ""),
  is_allowed_to_update: z
    .boolean()
    .nullable()
    .transform((value) => value ?? false),
});

export type UpdateAdminTenantParam = z.infer<typeof UpdateAdminTenantParamSchema>;
export const UpdateAdminTenantParamSchema = z.object({
  pictures: z.array(z.string()).nullable(),
  logo: z.string().nullable(),
  logo_url: z.string().nullable(),
  background_image: z.string().nullable(),
  enable_movie: z.boolean().nullable(),
  movie: z.string().nullable(),
  tenant_name: z.string().nullable(),
  tenant_description: z.string().nullable(),
  icon_color: z.string().nullable(),
});

export type MainMenuIcon = z.infer<typeof MainMenuIconSchema>;
export const MainMenuIconSchema = z.object({
  icon_code: z.string(),
  icon_url: z
    .string()
    .nullable()
    .transform((value) => value ?? ""),
  is_default: z.boolean(),
});

export type MainMenuDict = z.infer<typeof MainMenuDictSchema>;
export const MainMenuDictSchema = z.object({
  dict_code: z.string(),
  name: z.string(),
  is_default: z.boolean(),
});

export type QuerySubMenu = z.infer<typeof QuerySubMenuSchema>;
export const QuerySubMenuSchema = z.object({
  id: z.number(),
  tenant_id: z.number().optional(),
  admin_name_dict: z.string(),
  name_dict: z.string(),
  name_dict_status: TranslationStatusSchema.nullable()
    .optional()
    .transform((value) => value ?? null),
  menu_type: z.string(),
  sub_menu_type: z.string().nullable(),
  menu_option: z.string().nullable(),
  admin_name: z.string(),
  name: z.string(),
  is_display: z
    .boolean()
    .optional()
    .transform((value) => value ?? false),
  staff_groups: z.array(QueryStaffGroupStateSchema),
  location_groups: z.array(z.object({ color: z.string() })).default([]),
  not_show_list_inside: z.boolean().default(false),
  facility_id: z.number().nullable().optional(),
  lang_restriction_status: LangRestrictionStatusSchema.optional(),
});

// export type QueryStaff = z.infer<typeof QueryStaffSchema>;
// export const QueryStaffSchema = z.object({
//   id_account: z.number(),
//   tenant_id: z.number(),
//   name: z.string(),
//   state: z.string(),
//   role: z.string(),
//   is_deleted: z.boolean(),
//   belongs: z.array(QueryStaffBelongSchema),
//   user: QcUserSchema
// });

// export type QueryStaffGroup = z.infer<typeof QueryStaffGroupSchema>;
// export const QueryStaffGroupSchema = z.object({
//   id: z.number(),
//   tenant_id: z.number(),
//   name: z.string(),
//   order_no: z.number(),
//   staffs: z.array(QueryStaffSchema),
//   sub_menus: z.array(QuerySubMenuSchema)
// });

// export type QueryStaffBelong = z.infer<typeof QueryStaffBelongSchema>;
// export const QueryStaffBelongSchema = z.object({
//   id: z.number(),
//   staff_id_account: z.number(),
//   staff_group_id: z.number(),
//   staff_group: QueryStaffGroupSchema
// });

export type QueryLocationMainMenu = z.infer<typeof QueryLocationMainMenuSchema>;
export const QueryLocationMainMenuSchema = z.object({
  id: z.number(),
  location_group_id: z.number(),
  main_menu_id: z.number(),
  order_no: z.number(),
  sub_menus: z.array(QueryLocationSubMenuSchema),
});

export type QueryLocationGroup = z.infer<typeof QueryLocationGroupSchema>;
export const QueryLocationGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  display_name: z
    .string()
    .nullable()
    .transform((value) => value ?? ""),
  // display_name: z.string().nullable().default(""),
  tenant_id: z.number(),
  color: z.string(),
  is_default: z.boolean(),
  locations: z.array(QueryLocationSchema),
  main_menus: z.array(QueryLocationMainMenuSchema),
  position: z.number(),
  is_show_chat_history: z.boolean(),
});

export type QueryLocationGroupWithoutRelations = z.infer<typeof QueryLocationGroupWithoutRelationsSchema>;
export const QueryLocationGroupWithoutRelationsSchema = QueryLocationGroupSchema.omit({
  locations: true,
  main_menus: true,
  position: true,
});

export type QueryTenant = z.infer<typeof QueryTenantSchema>;
export const QueryTenantSchema = z.object({
  id: z.number(),
  code: z.string(),
  name_dict: z.string(),
  description_dict: z.string(),
  lang_cd: z.string(),
  pictures: z.array(z.string()),
  logo: z.string(),
  background_image: z.string(),
  qr_invite_staff: z.string(),
  admin_tenant_flg: z.boolean(),
  is_client: z.boolean(),
  cc_status_visible: z.boolean(),
  survey_rating_enabled: z.boolean(),
  inquiry_reject_enabled: z.boolean(),
  names: z.array(QueryQcBizDictSchema).optional(),
  descriptions: z.array(QueryQcBizDictSchema).optional(),
  main_menus: z.array(QueryMainMenuSchema),
  sub_menus: z.array(QuerySubMenuSchema),
  location_groups: z.array(QueryLocationGroupSchema),
  widget_on_off: z.boolean(),
  dch_hotel_id: z.string(),
});

export type Room = z.infer<typeof RoomSchema>;
export const RoomSchema = z.object({
  id: z.number(),
  location_group_name: z.string(),
  location_group_id: z.number(),
  name: z.string(),
  qr: z.string().optional(),
  hash_value: z.string().optional(),
  cms_flag: z.boolean().optional(),
});

export type StaffMember = z.infer<typeof StaffMemberSchema>;
export const StaffMemberSchema = z.object({
  id_account: z.number(),
  icon: z.string().nullable(),
  name: z.string(),
  email: z.string(),
  groups: z.array(z.string()),
});

export type StaffMember2 = z.infer<typeof StaffMember2Schema>;
export const StaffMember2Schema = z.object({
  id_account: z.number(),
  icon: z.string(),
  name: z.string(),
  email: z.string(),
  group_names: z.array(z.string()),
  is_deleted: z.boolean(),
});

export type AdminGroupQcUser = z.infer<typeof AdminGroupQcUserSchema>;
export const AdminGroupQcUserSchema = z.object({
  id_account: z.number(),
  access_id: z.string(),
  name: z.string(),
  lang_cd: z.string(),
  icon: z.string().nullable(),
  email: z.string(),
});

export type AdminGroupBelong = z.infer<typeof AdminGroupBelongSchema>;
export const AdminGroupBelongSchema = z.object({
  staff_group_id: z.number(),
  staff_id_account: z.number(),
  tenant_id: z.number(),
  name: z.string(),
});

export type FacilityTitle = z.infer<typeof FacilityTitleSchema>;
export const FacilityTitleSchema = z.object({
  id: z.number(),
  titleDict: z.string(),
  subTitles: z.array(z.string()),
});

export type QueryInviteStaffQr = z.infer<typeof QueryInviteStaffQrSchema>;
export const QueryInviteStaffQrSchema = z.object({
  url: z.string(),
  qr_image_url: z.string(),
});

export type SubMenuName = z.infer<typeof SubMenuNameSchema>;
export const SubMenuNameSchema = z.object({
  dictCd: z.string(),
  label: z.string(),
});

export type StaffGroupSet = z.infer<typeof StaffGroupSetSchema>;
export const StaffGroupSetSchema = z.object({
  id: z.number(),
  label: z.string(),
});

export type AdminTypeSplash = z.infer<typeof AdminTypeSplashSchema>;
export const AdminTypeSplashSchema = z.object({
  backgroundImage: z.string(),
});

export type AdminTypeDialog = z.infer<typeof AdminTypeDialogSchema>;
export const AdminTypeDialogSchema = z.object({
  dialog: z.boolean(),
  messageType: z.nativeEnum(AdminDialogMessage).optional(),
  message: z.string().optional(),
  btnColor: z.string().optional(),
  msgStyle: z.string().optional(),
});

export type SubMenuInUse = z.infer<typeof SubMenuInUseSchema>;
export const SubMenuInUseSchema = z.object({
  id: z.number(),
  adminName: z.string(),
  adminNameDict: z.string(),
  name: z.string(),
  nameDictStatus: TranslationStatusSchema.nullable(),
  nameDict: z.string(),
  options: z.string().nullable(),
  orderNo: z.number(),
  menuType: z.string(),
  subMenuType: z.string().nullable(),
  staffGroups: z.array(StaffGroupSetSchema),
  locationGroups: z.array(z.object({ color: z.string() })),
  isNameChanged: z.boolean(),
  isAdded: z.boolean(),
  isDisplay: z.boolean(),
});

// export type GeneralResponse = {
//   message: z.string(),
//   url: z.string(),
//   default_limit: z.number(),
// };

export type MainMenuIconDict = z.infer<typeof MainMenuIconDictSchema>;
export const MainMenuIconDictSchema = z.object({
  main_menu_id: z.number(),
  main_menu_code: z.string(),
  main_menu_icons: z.array(MainMenuIconSchema),
  main_menu_dicts: z.array(MainMenuDictSchema),
});

export type MainMenuInUse = z.infer<typeof MainMenuInUseSchema>;
export const MainMenuInUseSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
  orderNo: z.number(),
  dictCd: z.string(),
  iconCode: z.string(),
  requiredShowSubmenu: z.boolean(),
  subMenus: z.array(SubMenuInUseSchema),
  iconDict: MainMenuIconDictSchema,
});

export type AdminTypeMenu = z.infer<typeof AdminTypeMenuSchema>;
export const AdminTypeMenuSchema = z.object({
  allSubMenus: z.array(QuerySubMenuSchema),
  currentMainMenu: MainMenuInUseSchema.nullable(),
  currentGroup: QueryLocationGroupSchema,
  allStaffGroups: z.array(QueryStaffGroupStateSchema),
  nameDicts: z.array(QcBizDictSchema),
  currentNamesMap: z.map(z.number(), z.array(SubMenuNameSchema)),
});

export type UserDictItem = z.infer<typeof UserDictItemSchema>;
export const UserDictItemSchema = z.object({
  dict_cd: z.string(),
  messages: z.array(UserDictMessageSchema),
});

export type UserDictList = z.infer<typeof UserDictListSchema>;
export const UserDictListSchema = z.object({
  tenant_id: z.number().default(0),
  page: z.number().default(0),
  total_page: z.number().default(0),
  items: z.array(UserDictItemSchema),
});

export type HeaderMenu = z.infer<typeof HeaderMenuSchema>;
export const HeaderMenuSchema = z.object({
  type: z.string(),
  label: z.string(),
  code: z.string().optional(),
  codes: z.array(z.string()).optional(),
  menus: z.array(SubHeaderMenuSchema).optional(),
});

export type QueryTenantForDesignPage = z.infer<typeof QueryTenantForDesignPageSchema>;
export const QueryTenantForDesignPageSchema = z.object({
  id: z.number(),
  code: z.string(),
  name_dict: z.string(),
  description_dict: z.string(),
  ask_location_type: z.string(),
  pictures: z.array(z.string()),
  logo: z.string(),
  logo_url: z.string(),
  background_image: z.string(),
  enable_movie: z.boolean(),
  movie: z.string().nullable(),
  qr_invite_staff: z.string(),
  admin_tenant_flg: z.boolean(),
  default_staff_icon: z.string(),
  icon_color: z.string(),
  location_groups: z.array(QueryLocationGroupSchema),
});

export type SystemMessage = z.infer<typeof SystemMessageSchema>;
export const SystemMessageSchema = z.object({
  dict_cd: z.string(),
  contents: z.string(),
  variables: z.array(SystemMessageVariableSchema),
  translation_status: SystemMessageDictStatusSchema,
  updated_dict_lang_cds: z.array(z.string()).optional(),
});

export type AdminGroupStaff = z.infer<typeof AdminGroupStaffSchema>;
export const AdminGroupStaffSchema = z.object({
  id_account: z.number(),
  tenant_id: z.number(),
  is_deleted: z.boolean(),
  belongs: z.array(AdminGroupBelongSchema),
  user: AdminGroupQcUserSchema,
});

export type AdminStaffGroups = {
  id: number;
  tenant_id: number;
  name: string;
  orderNo: number;
  staffs: StaffMember[];
  allStaffs: AdminGroupStaff[];
  subMenus: AdminGroupSubMenus[];
  added: boolean;
};

export type QcCustomDictionary = z.infer<typeof QcCustomDictionarySchema>;
export const QcCustomDictionarySchema = z.object({
  dict_id: z.string(),
  id: z.number(),
  items: UserDictListSchema,
  state: z.string(),
  target_id: z.string(),
  target_type: z.string(),
});
